import type {
  Competition,
  Direction,
  LessonTypeRef,
  News,
  RegionRef,
  School,
  Trainer,
} from '~/api'
import { MSK_REGION_ID } from '~/stores/common'
import type { SearchParams } from '~/types'

export const useDirectoryStore = defineStore(
  'directory',
  () => {
    const api = useApi()

    const commonStore = useCommonStore()
    const regionId = computed(() => commonStore.currentRegionId || '')

    const regions = ref<RegionRef[]>([])

    const directions = ref<Partial<Direction>[]>([
      { name: 'Футбол' },
      { name: 'Баскетбол' },
      { name: 'Плавание' },
      { name: 'Волейбол' },
      { name: 'Гимнастика' },
      { name: 'Шахматы' },
      { name: 'Хоккей' },
    ])
    const lessonTypes = ref<Partial<LessonTypeRef>[]>([])
    const schools = ref<School[]>([])
    const trainers = ref<Trainer[]>([])
    const competitions = ref<Competition[]>([])
    const news = ref<News[]>([])

    interface ICity {
      label: string
    }

    const cities = ref<ICity[]>([
      { label: 'Екатеринбург' },
      { label: 'Казань' },
      { label: 'Краснодар' },
      { label: 'Нижний Новгород' },
      { label: 'Новосибирск' },
      { label: 'Омск' },
      { label: 'Ростов-на-Дону' },
      { label: 'Самара' },
      { label: 'Санкт-Петербург' },
      { label: 'Челябинск' },
    ])

    const selectedCity = ref<ICity | null>(null)

    const fetchRegions = async () => {
      try {
        regions.value = await $fetch<RegionRef[]>('/api/regions')
      } catch (error) {
        console.warn(
          'Не удалось загрузить регионы, используется fallback:',
          error,
        )
        regions.value = [
          { id: MSK_REGION_ID, name: 'Москва', full_name: 'Москва' },
        ]
      }
    }

    const fetchSchools = async (params?: SearchParams) => {
      if (!regionId.value) return
      const { data } = await api.public.schools({
        region_id: regionId.value,
        ...(params || {}),
      })
      schools.value = data.items
    }

    const fetchDirections = async (_params?: SearchParams) => {
      directions.value = await $fetch<Direction[]>('/api/directions')
    }

    const fetchLessonTypes = async () => {
      lessonTypes.value = await $fetch<LessonTypeRef[]>('/api/lesson-types')
    }

    const fetchTrainers = async (params?: SearchParams) => {
      if (!regionId.value) return
      const { data } = await api.public.trainers({
        region_id: regionId.value,
        ...(params || {}),
      })
      trainers.value = data.items
    }

    const fetchCompetitions = async () => {
      if (!regionId.value) return
      const { data } = await api.public.competitions({
        region_id: regionId.value,
      })
      competitions.value = data.items
    }

    const fetchNews = async (params?: SearchParams) => {
      if (!regionId.value) return
      const { data } = await api.public.news({
        region_id: regionId.value,
        ...(params || {}),
      })
      news.value = data.items
    }

    watch(selectedCity, (newValue, oldValue) => {
      if (import.meta.client) {
        if (
          newValue?.label &&
          oldValue?.label &&
          oldValue.label !== newValue.label
        ) {
          window.location.reload()
        }
      }
    })

    return {
      regions,
      cities,
      selectedCity,
      directions,
      lessonTypes,
      schools,
      trainers,
      competitions,
      news,

      fetchTrainers,
      fetchCompetitions,
      fetchDirections,
      fetchLessonTypes,
      fetchNews,
      fetchRegions,
      fetchSchools,
    }
  },
  {
    persist: import.meta.client
      ? {
          storage: piniaPluginPersistedstate.localStorage(),
          pick: ['selectedCity'],
        }
      : false,
  },
)
